import { useEffect, useMemo, useState } from 'react';
import {
  isDisplayablePhotoBlob,
  PROFILE_PHOTO_MAX_BYTES,
} from '../../utils/imageUtils';

type UseProfilePhotoFormStateOptions = {
  loadPhoto: () => Promise<Blob | null | undefined>;
  translate: (key: string) => string;
  invalidTypeKey: string;
  tooLargeKey: string;
  maxBytes?: number;
  reloadKey?: string | number | null;
};

export function useProfilePhotoFormState({
  loadPhoto,
  translate,
  invalidTypeKey,
  tooLargeKey,
  maxBytes = PROFILE_PHOTO_MAX_BYTES,
  reloadKey,
}: UseProfilePhotoFormStateOptions) {
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [removePhoto, setRemovePhoto] = useState(false);
  const [hadInitialPhoto, setHadInitialPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);

  const pendingSrc = useMemo(
    () => (pendingFile ? URL.createObjectURL(pendingFile) : null),
    [pendingFile],
  );

  useEffect(() => {
    if (!pendingSrc) {
      return;
    }
    return () => {
      URL.revokeObjectURL(pendingSrc);
    };
  }, [pendingSrc]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;

    // Reset local edits when the loaded profile changes, before fetching photo.
    /* eslint-disable react-hooks/set-state-in-effect -- sync reset on reloadKey/loadPhoto change */
    setPendingFile(null);
    setRemovePhoto(false);
    setPhotoError(null);
    /* eslint-enable react-hooks/set-state-in-effect */

    void (async () => {
      try {
        const blob = await loadPhoto();
        if (cancelled) {
          return;
        }

        if (isDisplayablePhotoBlob(blob)) {
          objectUrl = URL.createObjectURL(blob);
          setLoadedSrc(objectUrl);
          setHadInitialPhoto(true);
          return;
        }

        setLoadedSrc(null);
        setHadInitialPhoto(false);
      } catch {
        if (!cancelled) {
          setLoadedSrc(null);
          setHadInitialPhoto(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [loadPhoto, reloadKey]);

  const handlePhotoChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<File | null> => {
    const file = event.target.files?.[0];
    event.target.value = '';

    if (!file) {
      return null;
    }

    if (!file.type.startsWith('image/')) {
      setPhotoError(translate(invalidTypeKey));
      return null;
    }

    if (file.size > maxBytes) {
      setPhotoError(translate(tooLargeKey));
      return null;
    }

    setPendingFile(file);
    setRemovePhoto(false);
    setPhotoError(null);
    return file;
  };

  const handleRemovePhoto = () => {
    setPendingFile(null);
    setRemovePhoto(true);
    setPhotoError(null);
  };

  const resetPhotoEdits = () => {
    setPendingFile(null);
    setRemovePhoto(false);
    setPhotoError(null);
  };

  const photoChanged = pendingFile != null || (removePhoto && hadInitialPhoto);
  const previewSrc = removePhoto ? null : (pendingSrc ?? loadedSrc);

  return {
    previewSrc,
    photoError,
    photoChanged,
    pendingFile,
    removePhoto,
    handlePhotoChange,
    handleRemovePhoto,
    resetPhotoEdits,
  };
}
